import { useCallback } from "react";

function shouldLetBrowserHandle(event) {
  if (!event) {
    return false;
  }

  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

export default function useUnreadModalNavigation({
  activeType,
  onClose,
  projectIndexUrl,
  jobIndexUrl,
}) {
  const isProject = activeType === "projects";
  const indexPath = isProject ? projectIndexUrl : jobIndexUrl;

  const navigateSafely = useCallback((path) => {
    if (typeof path !== "string" || path.trim() === "") {
      return false;
    }

    try {
      const parsedUrl = new URL(path, window.location.href);
      const isAllowedProtocol =
        parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";

      if (!isAllowedProtocol) {
        return false;
      }

      if (parsedUrl.origin === window.location.origin) {
        // NOTE: Header は Router 外（別mount）で動いているため react-router の navigate() は使用不可
        // pushState は popstate を発火しないので、BrowserRouter に URL 変更を検知させる目的で popstate を手動発火
        const nextPath =
          parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
        window.history.pushState({}, "", nextPath);
        window.dispatchEvent(new PopStateEvent("popstate"));
        return true;
      }

      window.location.assign(parsedUrl.href);
      return true;
    } catch {
      // 不正な URL は遷移しない
      return false;
    }
  }, []);

  const navigateByEvent = useCallback(
    (path, event) => {
      if (shouldLetBrowserHandle(event)) {
        return;
      }

      event?.preventDefault?.();

      const hasNavigated = navigateSafely(path);

      if (hasNavigated) {
        onClose?.();
      }
    },
    [navigateSafely, onClose]
  );

  const buildDetailPath = useCallback(
    (itemId) => {
      return isProject ? `/projects/${itemId}` : `/jobs/${itemId}`;
    },
    [isProject]
  );

  const onClickIndex = useCallback((event) => {
    navigateByEvent(indexPath, event);
  }, [navigateByEvent, indexPath]);

  const onClickItem = useCallback(
    (itemId, event) => {
      const detailPath = buildDetailPath(itemId);
      navigateByEvent(detailPath, event);
    },
    [buildDetailPath, navigateByEvent]
  );

  const onKeyboardSelectItem = useCallback(
    (itemId) => {
      const detailPath = buildDetailPath(itemId);
      const hasNavigated = navigateSafely(detailPath);
  
      if (hasNavigated) {
        onClose?.();
      }
    },
    [buildDetailPath, navigateSafely, onClose]
  );

  const onItemKeyDown = useCallback(
    (itemId) => (event) => {
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        onKeyboardSelectItem(itemId);
      }
    },
    [onKeyboardSelectItem]
  );

  return { isProject, onClickIndex, onClickItem, onItemKeyDown, indexPath };
}